import React from "react";
import { createMonitoringAPICall } from "../../../utils/createMonitoringAPICall";
import { AxiosError } from "axios";
import { timeSlicer } from "../timeSlicer";
import { logger } from "../../../../utils/logger";

// Archived-range reports (ENG-1842) run in Athena: measured at 1.4–7s, but give
// a big range room. Each poll is NOT cheap for the backend — every retry
// re-enters download_v2 (outcome rows, site rows, SQL construction, two Athena
// API calls) — so back off: 2s catches the common case in one retry, then the
// interval grows toward a 15s ceiling to keep long generations light.
export const REPORT_POLL_INITIAL_MS = 2000;
export const REPORT_POLL_MAX_MS = 15000;
export const REPORT_POLL_TIMEOUT_MS = 5 * 60 * 1000;

const abortableSleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });

export const getAlertsDownloadURL = async (
  groupId: string,
  siteId: string,
  cameraId: string,
  startDate: string,
  endDate: string,
  startTime: Date,
  endTime: Date,
  signal?: AbortSignal
): Promise<string> => {
  const AxiosUI = createMonitoringAPICall(``);

  let searchParamsString = ``;

  if (groupId) {
    if (groupId !== "undefined") {
      searchParamsString += `?group_id=${groupId}`;
    }
  }

  if (siteId) {
    if (siteId !== "undefined") {
      if (groupId !== "undefined") {
        searchParamsString += `&customer_id=${siteId}`;
      } else {
        searchParamsString += `?customer_id=${siteId}`;
      }
    }
  }

  if (cameraId) {
    if (cameraId !== "undefined") {
      if (groupId !== "undefined" && siteId !== "undefined") {
        searchParamsString += `&camera_id=${cameraId}`;
      } else {
        searchParamsString += `?camera_id=${cameraId}`;
      }
    }
  }

  if (startDate) {
    if (
      groupId !== "undefined" &&
      siteId !== "undefined" &&
      cameraId !== "undefined"
    ) {
      searchParamsString += `&start=${startDate}`;
    } else {
      searchParamsString += `?start=${startDate}`;
    }
  }

  if (endDate) {
    if (
      groupId !== "undefined" &&
      siteId !== "undefined" &&
      cameraId !== "undefined" &&
      startDate
    ) {
      searchParamsString += `&end=${endDate}`;
    } else {
      searchParamsString += `?end=${endDate}`;
    }
  }

  if (startDate.toString() === endDate.toString() && startTime !== null) {
    searchParamsString += `&start_time=${timeSlicer(startTime)}`;
  }

  if (startDate.toString() === endDate.toString() && endTime !== null) {
    searchParamsString += `&end_time=${timeSlicer(endTime)}`;
  }

  if (searchParamsString.length > 0) {
    searchParamsString += `&source=v2`;
  }

  try {
    // ENG-1842: for ranges older than the DynamoDB retention the backend
    // generates the report asynchronously in Athena and answers 202
    // {status: "processing", poll_url} until it's ready. Repeating the same
    // request is the poll — the backend keys the query on the request
    // parameters, so every retry lands on the same report. The caller's
    // AbortSignal stops the loop on unmount or filter change; abandoning the
    // poll never abandons the report (it finishes server-side and a later
    // identical request picks it up).
    const deadline = Date.now() + REPORT_POLL_TIMEOUT_MS;
    let interval = REPORT_POLL_INITIAL_MS;
    for (;;) {
      const response = await AxiosUI.get(
        `alert/download_v2/${searchParamsString}`,
        { signal }
      );
      if (response.status !== 202) {
        return response.data.url;
      }
      if (Date.now() > deadline) {
        throw new Error(
          "Report generation timed out. Please try fetching it again."
        );
      }
      await abortableSleep(interval, signal);
      interval = Math.min(interval * 2, REPORT_POLL_MAX_MS);
    }
  } catch (error) {
    const axiosError = error as AxiosError;
    logger.log("error", axiosError.message);
    throw error;
  }
};
